import { useState, useRef, useCallback } from 'react';
import { useConstructionPhotos, useUploadConstructionPhotos, useDeleteConstructionPhoto } from '@saiens/shared/hooks';
import { toast } from '@saiens/shared/stores';
import { LoadingSpinner } from '@/components/common/LoadingSpinner';
import { Camera, ImagePlus, X, Trash2, ZoomIn } from 'lucide-react';
import type { ConstructionPhoto } from '@saiens/shared/types';

interface PhotoSectionProps {
  orderId: number;
}

/**
 * Photo Section Component for Construction Order Detail Page
 *
 * Displays uploaded photos and allows camera/gallery upload.
 */
export function PhotoSection({ orderId }: PhotoSectionProps) {
  const fileInputRef = useRef<HTMLInputElement>(null);
  const cameraInputRef = useRef<HTMLInputElement>(null);

  const [selectedImages, setSelectedImages] = useState<string[]>([]);
  const [showLightbox, setShowLightbox] = useState(false);
  const [lightboxUrl, setLightboxUrl] = useState<string | null>(null);

  const { data: photos, isLoading } = useConstructionPhotos(orderId);
  const uploadPhotos = useUploadConstructionPhotos();
  const deletePhoto = useDeleteConstructionPhoto();

  const handleFileSelect = useCallback((e: React.ChangeEvent<HTMLInputElement>) => {
    const files = e.target.files;
    if (!files || files.length === 0) return;

    const newImages: string[] = [];
    const validFiles: File[] = [];

    // Validate all files first
    for (let i = 0; i < files.length; i++) {
      const file = files[i];

      // Validate file type
      if (!file.type.startsWith('image/')) {
        toast.error('檔案格式錯誤', `${file.name} 不是圖片檔案`);
        continue;
      }

      // Validate file size (max 10MB)
      if (file.size > 10 * 1024 * 1024) {
        toast.error('檔案過大', `${file.name} 超過 10MB 限制`);
        continue;
      }

      validFiles.push(file);
    }

    if (validFiles.length === 0) {
      e.target.value = '';
      return;
    }

    // Check total count
    if (selectedImages.length + validFiles.length > 10) {
      toast.error('超過限制', '單次最多上傳 10 張照片');
      e.target.value = '';
      return;
    }

    // Read all valid files
    let processed = 0;
    validFiles.forEach((file) => {
      const reader = new FileReader();
      reader.onerror = () => {
        toast.error('讀取失敗', `無法讀取 ${file.name}`);
        processed++;
        if (processed === validFiles.length) {
          setSelectedImages((prev) => [...prev, ...newImages]);
        }
      };
      reader.onload = (event) => {
        const result = event.target?.result as string;
        if (result) {
          newImages.push(result);
        }
        processed++;
        if (processed === validFiles.length) {
          setSelectedImages((prev) => [...prev, ...newImages]);
        }
      };
      reader.readAsDataURL(file);
    });

    // Reset input
    e.target.value = '';
  }, [selectedImages.length]);

  const handleCameraCapture = () => {
    cameraInputRef.current?.click();
  };

  const handleGallerySelect = () => {
    fileInputRef.current?.click();
  };

  const handleRemoveSelectedImage = (index: number) => {
    setSelectedImages((prev) => prev.filter((_, i) => i !== index));
  };

  const handleUpload = () => {
    if (selectedImages.length === 0) return;

    // Extract base64 data from data URLs
    const base64Images = selectedImages.map((img) => {
      if (img.includes(',')) {
        return img.split(',')[1];
      }
      return img;
    });

    uploadPhotos.mutate(
      { orderId, images: base64Images },
      {
        onSuccess: () => {
          setSelectedImages([]);
        },
      }
    );
  };

  const handleDeletePhoto = (photoId: number) => {
    if (!confirm('確定要刪除這張照片嗎？')) return;
    deletePhoto.mutate({ orderId, photoId });
  };

  const openLightbox = (url: string) => {
    setLightboxUrl(url);
    setShowLightbox(true);
  };

  const closeLightbox = () => {
    setShowLightbox(false);
    setLightboxUrl(null);
  };

  return (
    <div className="rounded-xl bg-white p-4 shadow-sm">
      {/* Hidden file inputs */}
      <input
        ref={cameraInputRef}
        type="file"
        accept="image/*"
        capture="environment"
        onChange={handleFileSelect}
        className="hidden"
        aria-label="Camera capture"
      />
      <input
        ref={fileInputRef}
        type="file"
        accept="image/*"
        multiple
        onChange={handleFileSelect}
        className="hidden"
        aria-label="Gallery select"
      />

      <div className="mb-3 flex items-center justify-between">
        <h2 className="font-semibold text-gray-900">施工照片</h2>
        <div className="flex gap-2">
          <button
            onClick={handleCameraCapture}
            disabled={uploadPhotos.isPending}
            className="flex items-center gap-1 rounded-lg bg-primary px-3 py-1.5 text-sm text-white hover:bg-primary/90 disabled:bg-gray-300"
          >
            <Camera className="h-4 w-4" />
            拍照
          </button>
          <button
            onClick={handleGallerySelect}
            disabled={uploadPhotos.isPending}
            className="flex items-center gap-1 rounded-lg border border-gray-300 bg-white px-3 py-1.5 text-sm text-gray-700 hover:bg-gray-50 disabled:bg-gray-100"
          >
            <ImagePlus className="h-4 w-4" />
            相簿
          </button>
        </div>
      </div>

      {/* Selected Images Preview */}
      {selectedImages.length > 0 && (
        <div className="mb-4">
          <p className="mb-2 text-sm text-gray-500">待上傳 ({selectedImages.length} 張)</p>
          <div className="grid grid-cols-4 gap-2">
            {selectedImages.map((img, idx) => (
              <div key={idx} className="relative aspect-square">
                <img
                  src={img}
                  alt={`待上傳 ${idx + 1}`}
                  className="h-full w-full rounded-lg object-cover"
                />
                <button
                  onClick={() => handleRemoveSelectedImage(idx)}
                  className="absolute -right-1 -top-1 rounded-full bg-red-500 p-1 text-white shadow"
                >
                  <X className="h-3 w-3" />
                </button>
              </div>
            ))}
          </div>
          <button
            onClick={handleUpload}
            disabled={uploadPhotos.isPending}
            className="mt-3 flex w-full items-center justify-center gap-2 rounded-lg bg-primary py-2 text-sm font-medium text-white hover:bg-primary/90 disabled:bg-gray-300"
          >
            {uploadPhotos.isPending ? (
              <>
                <LoadingSpinner size="sm" />
                上傳中...
              </>
            ) : (
              `上傳 ${selectedImages.length} 張照片`
            )}
          </button>
        </div>
      )}

      {/* Uploaded Photos */}
      {isLoading ? (
        <div className="flex justify-center py-6">
          <LoadingSpinner size="md" />
        </div>
      ) : photos && photos.length > 0 ? (
        <div className="grid grid-cols-3 gap-2">
          {photos.map((photo) => (
            <PhotoThumbnail
              key={photo.id}
              photo={photo}
              onView={() => openLightbox(photo.url)}
              onDelete={() => handleDeletePhoto(photo.id)}
              isDeleting={deletePhoto.isPending}
            />
          ))}
        </div>
      ) : selectedImages.length === 0 ? (
        <div className="rounded-lg bg-gray-50 py-6 text-center text-sm text-gray-500">
          尚未上傳照片
        </div>
      ) : null}

      {/* Lightbox */}
      {showLightbox && lightboxUrl && (
        <div
          className="fixed inset-0 z-50 flex items-center justify-center bg-black/80"
          onClick={closeLightbox}
        >
          <button
            onClick={closeLightbox}
            className="absolute right-4 top-4 rounded-full bg-white/20 p-2 text-white"
          >
            <X className="h-6 w-6" />
          </button>
          <img
            src={lightboxUrl}
            alt="照片預覽"
            className="max-h-[90vh] max-w-[90vw] object-contain"
            onClick={(e) => e.stopPropagation()}
          />
        </div>
      )}
    </div>
  );
}

interface PhotoThumbnailProps {
  photo: ConstructionPhoto;
  onView: () => void;
  onDelete: () => void;
  isDeleting: boolean;
}

function PhotoThumbnail({ photo, onView, onDelete, isDeleting }: PhotoThumbnailProps) {
  return (
    <div className="group relative aspect-square overflow-hidden rounded-lg bg-gray-100">
      <img
        src={photo.url}
        alt={photo.name}
        className="h-full w-full object-cover"
        loading="lazy"
      />
      {/* Overlay on hover */}
      <div className="absolute inset-0 flex items-center justify-center gap-2 bg-black/50 opacity-0 transition-opacity group-hover:opacity-100">
        <button
          onClick={onView}
          className="rounded-full bg-white/20 p-2 text-white hover:bg-white/30"
        >
          <ZoomIn className="h-4 w-4" />
        </button>
        <button
          onClick={onDelete}
          disabled={isDeleting}
          className="rounded-full bg-red-500/80 p-2 text-white hover:bg-red-500 disabled:bg-gray-400"
        >
          {isDeleting ? (
            <LoadingSpinner size="sm" />
          ) : (
            <Trash2 className="h-4 w-4" />
          )}
        </button>
      </div>
      {/* Upload info */}
      {photo.uploaded_by && (
        <div className="absolute bottom-0 left-0 right-0 bg-gradient-to-t from-black/60 to-transparent px-2 py-1">
          <p className="truncate text-xs text-white">{photo.uploaded_by}</p>
        </div>
      )}
    </div>
  );
}
